/**
 * 明細列 → 要印的表身列(REQ-20260817003 §三.2 起,REQ-20260831002 抽成共用檔)。
 *
 * 原本這段長在 `GroupedCustomerStatements.tsx` 裡。REQ-20260831001 §六 記過
 * 「第三個版費呼叫點出現時再抽共用」—— `ArDetailItemTable` 就是第三個,所以抽在這裡。
 * **內容原樣搬移**,只多帶一個 `unitPrice`(分組版面不顯示單價,填了也不影響它)。
 *
 * ⚠️ 月結對帳單(`features/statements/StatementView.tsx`)另有一份同名函式,
 * 吃的是 `StatementLine` 不是 `ArApDetailRow`,**不在本次範圍**、刻意沒有合併。
 */
import type { ArApDetailItem, ArApDetailRow } from '@app/shared';

/** 表身的一列 —— 品項、單頭費用、或「這張單沒有品項」的替身 */
export interface PrintLine {
  key: string;
  categoryName: string | null;
  productName: string | null;
  qty: string | null;
  unitName: string | null;
  /** 品項列填 `item.unitPrice`;沒有品項的歷史單與版費列沒有單價,填 null */
  unitPrice: string | null;
  amount: string;
}

/**
 * 一張單 → 要印的列。
 *
 * 版費 / 刀模費是**單頭欄位不是品項**(`schema.prisma` 的 `plateFee` / `dieFee`,
 * 併入 `subtotal` 一起課稅),所以只印品項的話,有版費的單會少請那筆錢。
 * 補一列把差額印出來,「小計 = 印出來的金額欄加總 = 各單 subtotal 加總」才成立。
 */
export function printLines(row: ArApDetailRow): PrintLine[] {
  const items = row.items ?? [];
  // 歷史 mdb 匯入單可能只有單頭、沒有品項 —— 仍要印一列,
  // 否則單據金額進了小計、明細裡卻找不到對應的一行
  if (items.length === 0) {
    return [
      {
        key: `order-${row.orderId}`,
        categoryName: null,
        productName: null,
        qty: null,
        unitName: null,
        unitPrice: null,
        amount: row.subtotal,
      },
    ];
  }

  const lines: PrintLine[] = items.map((item: ArApDetailItem) => ({
    key: `line-${item.lineId}`,
    categoryName: item.productCategoryName,
    productName: item.productName,
    qty: item.qty,
    unitName: item.unitName,
    unitPrice: item.unitPrice,
    amount: item.amount,
  }));

  // 先四捨五入到分,否則浮點誤差會讓每張單都長出一列 0 元的「版費」
  const fees =
    Math.round((Number(row.subtotal) - items.reduce((sum, i) => sum + Number(i.amount), 0)) * 100) /
    100;
  if (fees !== 0) {
    lines.push({
      key: `fees-${row.orderId}`,
      categoryName: null,
      productName: '版費 / 刀模費',
      qty: null,
      unitName: null,
      unitPrice: null,
      amount: String(fees),
    });
  }
  return lines;
}

/**
 * 數量是 DECIMAL(14,4) —— 不能走 `formatMoney`(REQ-20260814003 之後那支會
 * 四捨五入成整數,0.5 打包就變成 1)。
 */
export function formatQty(qty: string): string {
  return Number(qty).toLocaleString('zh-TW', { maximumFractionDigits: 4 });
}
